// Centralized plain-text normalization for messaging fields and single-line
// titles/names. Mirrors the backend `stripHtmlForCsv` entity set
// (backend/modules/trivia/triviaService.js). Used both at save-time (so new
// records are never persisted dirty) and at display-time (so existing dirty
// Mongo records render clean).
//
// Do NOT reuse `contvertHTMLToPlain` (utils/constants.js): its `unescapeString`
// only handles JSON-escaped strings and does not decode HTML entities such as
// `&nbsp;` / `&amp;`, which is the root cause of the reported dirty output.

// HTML entities we decode. Order matters only in that `&amp;` is decoded once
// in a single pass over the source string, so an already-clean literal `&`
// (e.g. "R&D") is left untouched and is NOT re-encoded — keeping these helpers
// idempotent for already-clean text.
const HTML_ENTITIES = {
  '&nbsp;': ' ',
  '&#160;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

// Single-pass entity decode so we never double-decode (e.g. "&amp;lt;" stays
// "&lt;" rather than collapsing to "<").
const decodeEntities = (value) => {
  return value.replace(/&(?:nbsp|#160|amp|lt|gt|quot|#39);/g, (match) => {
    const decoded = HTML_ENTITIES[match];
    return decoded === undefined ? match : decoded;
  });
};

// Strip only recognized HTML tags emitted by the rich-text editors (Quill /
// CKEditor). Matching by tag name — rather than the naive /<[^>]+>/g — means a
// user-typed literal like "R&D < Sales > Marketing" is preserved instead of
// being mistaken for a tag and deleted.
const HTML_TAG_PATTERN = new RegExp(
  '</?(?:' +
    'p|br|div|span|strong|b|em|i|u|s|strike|del|ins|sub|sup|' +
    'ol|ul|li|a|h[1-6]|blockquote|pre|code|font|small|mark' +
    ')(?:\\s[^<>]*)?/?>',
  'gi'
);

const stripTags = (value) => {
  return value.replace(HTML_TAG_PATTERN, '');
};

const isEmptyValue = (value) => {
  return value === null || value === undefined;
};

/**
 * Remove only the Unicode replacement character (U+FFFD). Exported for the CSV
 * upload path where firstName/lastName are read before any other normalization
 * is wanted. Valid accented UTF-8 characters (José, Müller) are untouched.
 */
export const stripReplacementChar = (value) => {
  if (isEmptyValue(value)) {
    return '';
  }
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }
  return value.replace(/�/g, '');
};

/**
 * Normalize a multiline messaging value (messagingSubject / message body).
 * - converts <br>, <br/>, <br /> to real newlines
 * - strips remaining HTML tags
 * - decodes HTML entities
 * - collapses runs of spaces/tabs to a single space (PRESERVES newlines)
 * - trims leading/trailing whitespace
 * Idempotent for already-clean text. Returns "" for null/undefined.
 */
export const normalizeMessageText = (value) => {
  if (isEmptyValue(value)) {
    return '';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value !== 'string' || value.length === 0) {
    return '';
  }

  let result = value.replace(/<br\s*\/?>/gi, '\n');
  // Convert block-level closers to newlines so paragraph structure survives
  // (Quill emits <p>…</p> per paragraph); without this, adjacent blocks glue
  // together ("A</p><p>B" -> "AB").
  result = result.replace(/<\/(?:p|div|h[1-6]|li|blockquote)>/gi, '\n');
  result = stripTags(result);
  result = decodeEntities(result);
  // Collapse runs of spaces/tabs only, leaving newlines intact.
  result = result.replace(/[^\S\n]+/g, ' ');
  // Drop spaces around newlines, then cap blank lines at a single paragraph break.
  result = result.replace(/ *\n */g, '\n');
  result = result.replace(/\n{3,}/g, '\n\n');
  return result.trim();
};

/**
 * Like normalizeMessageText, but PRESERVES every horizontal space, indent, and
 * blank line: it converts HTML to plain text (so legacy HTML-dirty records
 * render clean) yet does NOT collapse runs of spaces/tabs and does NOT cap blank
 * lines. For rendering a message body where the exact authored spacing must
 * survive — e.g. the "Download Message" PDF shown in a `white-space: pre-wrap`
 * block — while still stripping stray markup. Only leading/trailing blank lines
 * (e.g. a trailing `</p>` artifact) are trimmed; internal spacing is untouched.
 * Idempotent for already-clean text. Returns "" for null/undefined.
 */
export const messageTextPreserveSpacing = (value) => {
  if (isEmptyValue(value)) {
    return '';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value !== 'string' || value.length === 0) {
    return '';
  }

  let result = value.replace(/<br\s*\/?>/gi, '\n');
  result = result.replace(/<\/(?:p|div|h[1-6]|li|blockquote)>/gi, '\n');
  result = stripTags(result);
  result = decodeEntities(result);
  // Trim only leading/trailing newlines (conversion artifacts / stray blank
  // lines), preserving every internal space, indent, and blank line.
  return result.replace(/^\n+/, '').replace(/\n+$/, '');
};

/**
 * Normalize a single-line value (name / title / firstName / lastName).
 * - strips HTML tags
 * - decodes HTML entities
 * - removes the Unicode replacement character (U+FFFD, "�") that appears when
 *   a non-UTF-8 CSV is read as UTF-8 (does NOT alter valid accents)
 * - collapses ALL whitespace (incl. newlines) to a single space
 * - trims leading/trailing whitespace
 * Idempotent for already-clean text. Returns "" for null/undefined.
 */
export const normalizeSingleLine = (value) => {
  if (isEmptyValue(value)) {
    return '';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value !== 'string' || value.length === 0) {
    return '';
  }

  let result = stripTags(value);
  result = decodeEntities(result);
  result = stripReplacementChar(result);
  result = result.replace(/\s+/g, ' ');
  return result.trim();
};

/**
 * Inverse of normalizeMessageText for the rich-text editor only: turn a clean
 * plain-text message (with real \n) into minimal safe HTML so ReactQuill — which
 * treats its `value` as HTML and collapses bare newlines — re-renders the line
 * breaks on load. HTML-special chars are escaped first so a literal "<", ">" or
 * "&" shows verbatim instead of being parsed as markup; on save, normalizeMessageText
 * converts the editor's HTML back to clean plain text, so the round-trip is lossless.
 * Returns "" for null/undefined.
 */
export const messageTextToHtml = (value) => {
  if (isEmptyValue(value)) {
    return '';
  }
  if (typeof value === 'number') {
    value = String(value);
  }
  if (typeof value !== 'string' || value.length === 0) {
    return '';
  }

  // The seed is normally clean plain text, but during a save it can transiently
  // be raw editor HTML (e.g. "<p>a</p><p>b</p>"). Normalize first so tags become
  // real newlines instead of being escaped and flashed as literal "<p>" text.
  const plain = normalizeMessageText(value);
  const escaped = plain
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(/\n/g, '<br>');
};

/**
 * Trim leading/trailing whitespace from a short display string such as an MCQ
 * option label — including `&nbsp;` and a space sitting just inside a leading
 * tag (e.g. "<p> Excellent"). Inner markup and inner spacing are preserved.
 *
 * Needed because the backend's `getOptionTextProper` (backend/constants.js) only
 * trims when its "A) " prefix regex matches; that regex requires the first
 * character to be a letter, so an option that arrives with a LEADING SPACE never
 * matches and is stored with the space. Such an option then renders indented
 * relative to its siblings. Returns "" for null/undefined, and non-strings
 * unchanged. Idempotent for already-clean text.
 */
export const trimDisplayText = (value) => {
  if (isEmptyValue(value)) {
    return '';
  }
  if (typeof value !== 'string') {
    return value;
  }

  const OUTER = '(?:\\s|&nbsp;|&#160;)+';
  return value
    .replace(new RegExp('^' + OUTER), '')
    .replace(new RegExp(OUTER + '$'), '')
    // a space immediately after a leading tag / before a trailing closing tag
    .replace(new RegExp('^(<[a-z][^>]*>)' + OUTER, 'i'), '$1')
    .replace(new RegExp(OUTER + '(</[a-z]+>)$', 'i'), '$1');
};

export default {
  stripReplacementChar,
  normalizeMessageText,
  normalizeSingleLine,
  messageTextToHtml,
  trimDisplayText,
};
